import { useState } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Box,
  Button,
  Chip,
  Fade,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Image from 'next/image';
import Router from 'next/router';
import { Icon } from '@iconify/react';
import GlobalSearchBar from './GlobalSearchBar';
import QuickSearchRow from './QuickSearchRow';
import { VignetteSection, VignetteConfig } from './VignetteSection';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerRoot: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      paddingTop: '4%',
      paddingBottom: '4%',
    },
    heroCard: {
      borderRadius: 20,
      border: '1px solid rgba(148, 163, 184, 0.14)',
      background: 'rgba(15, 23, 42, 0.72)',
      boxShadow: '0 24px 60px rgba(2, 6, 23, 0.35)',
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
    },
    brand: {
      paddingRight: 48,
      paddingLeft: 48,
      paddingBottom: 12,
      paddingTop: 24,
      marginTop: 8,
    },
    randomButton: {
      color: '#e2e8f0',
      textTransform: 'none',
      backgroundColor: '#312e81',
      '&:hover': {
        backgroundColor: '#3730a3',
      },
    },
    eyebrow: {
      color: '#818cf8',
      fontWeight: 700,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      fontSize: '0.78rem',
    },
    headline: {
      color: '#f8fafc',
      fontWeight: 800,
      fontSize: '3rem',
      lineHeight: 1.02,
      letterSpacing: '-0.04em',
      [theme.breakpoints.down('md')]: {
        fontSize: '2.2rem',
      },
    },
    subline: {
      color: '#94a3b8',
      fontWeight: 400,
      fontSize: '1.05rem',
      lineHeight: 1.7,
      maxWidth: 700,
    },
    typography: {
      marginTop: 8,
      color: '#cbd5e1',
      fontWeight: 600,
      fontSize: '1rem',
    },
    workflowCard: {
      background: 'rgba(2, 6, 23, 0.42)',
      border: '1px solid rgba(148, 163, 184, 0.12)',
      borderRadius: 16,
      padding: 18,
      height: '100%',
    },
    modeCard: {
      background: 'rgba(2, 6, 23, 0.55)',
      border: '1px solid rgba(99, 102, 241, 0.25)',
      borderRadius: 16,
      padding: 24,
      cursor: 'pointer',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      '&:hover': {
        borderColor: 'rgba(99, 102, 241, 0.55)',
        boxShadow: '0 0 28px rgba(99, 102, 241, 0.12)',
      },
    },
    clampText: {
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
  }),
);

// Page content width — widened so more of the viewport is used on large
// screens, reducing vertical scrolling for the same amount of content.
const PAGE_MAX_WIDTH = 1600;

type CapabilityConfig = {
  key: string;
  eyebrow: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  badge: string;
  tags: string[];
  buttonLabel: string;
  route: string;
};

// Capabilities surfaced on the dashboard. Each links out to /capabilities/*.
const CAPABILITIES: CapabilityConfig[] = [
  {
    key: 'mil-civ-fusion',
    eyebrow: 'Military-Civilian Fusion Analysis',
    title: 'MIL-CIV Fusion',
    description:
      'Visualize connections from companies, SOEs, universities, and research institutes to PLA threat actors. Use Quick Filters to discover entities working on Quantum, Radar, Missile, Semiconductor, and Biotechnology research.',
    icon: 'carbon:connection-signal',
    color: '#dc2626',
    badge: 'Threat Analysis',
    tags: ['Quantum', 'Radar', 'Missile', 'Semiconductor', 'Biotechnology'],
    buttonLabel: 'Open MIL-CIV Fusion Tool',
    route: '/capabilities/mil-civ-fusion',
  },
  {
    key: 'export-control-vetting',
    eyebrow: 'Homeland Security Workflow',
    title: 'PLA Procurement Vetting',
    description:
      'Investigate PLA procurement contracts for critical and emerging technologies. Detect PLA-style project numbers, extract suppliers and military end-users, and flag announcements that may reveal controlled hardware or software.',
    icon: 'carbon:security-services',
    color: '#38bdf8',
    badge: 'Compliance',
    tags: [
      'Project-code detection',
      'Supplier screening',
      'Military end-use review',
      'Critical-tech exposure',
    ],
    buttonLabel: 'Open PLA Procurement Vetting',
    route: '/capabilities/export-control-vetting',
  },
  {
    key: 'pla-yellow-pages',
    eyebrow: 'Defense Intelligence Directory',
    title: 'The PLA Yellow Pages',
    description:
      "Comprehensive directory of PLA-affiliated organizations, defense universities, research institutes, and state-owned enterprises across China's defense ecosystem.",
    icon: 'carbon:book',
    color: '#f59e0b',
    badge: 'Directory',
    tags: [
      'Defense Universities',
      'Research Institutes',
      'State Enterprises',
      'Seven Sons',
      'CAS Labs',
    ],
    buttonLabel: 'Open PLA Yellow Pages',
    route: '/capabilities/pla-yellow-pages',
  },
  {
    key: 'network-intelligence',
    eyebrow: 'Network Intelligence',
    title: 'Network Intelligence Workspace',
    description:
      'Map relationships between publications, organizations, technologies, and talent networks using a multi-modal force-directed graph explorer.',
    icon: 'carbon:network-3',
    color: '#818cf8',
    badge: 'Unified Platform',
    tags: [
      'Publications',
      'Affiliations',
      'Authors',
      'Risk Signals',
      'Impact Filters',
    ],
    buttonLabel: 'Open Network Intelligence Workspace',
    route: '/capabilities/network-intelligence',
  },
  {
    key: 'mariana',
    eyebrow: 'Embedded Analysis Tool',
    title: 'Mariana',
    description:
      'Purpose-built analysis surface embedded directly into the platform, giving analysts a focused workspace for specialized filters and deep-dive review.',
    icon: 'carbon:analytics',
    color: '#06b6d4',
    badge: 'Analysis Tool',
    tags: ['Specialized Filters', 'Focused Workflow', 'Deep-Dive Review'],
    buttonLabel: 'Open Mariana',
    route: '/capabilities/mariana',
  },
  {
    key: 'orbital-nexus',
    eyebrow: 'Geospatial Intelligence',
    title: 'Orbital Nexus',
    description:
      "Interactive geospatial platform that maps and explores relationships between China's space ecosystem, research projects, researchers, and affiliated PLA entities.",
    icon: 'carbon:satellite-radar',
    color: '#0ea5e9',
    badge: 'Geospatial',
    tags: ['Space Ecosystem', 'Research Projects', 'PLA Entities'],
    buttonLabel: 'Open Orbital Nexus',
    route: '/capabilities/orbital-nexus',
  },
  {
    key: 'defense-technology-intelligence',
    eyebrow: 'Fused Intelligence Graph',
    title: 'Defense Technology Intelligence',
    description:
      'Fuses the Publications/Research API with the PLA Contracts/Procurement API into a single interactive, evidence-backed intelligence graph spanning Organizations, Research, Technology, PLA, Procurement, and Suppliers.',
    icon: 'carbon:machine-learning-model',
    color: '#dc2626',
    badge: 'Premium',
    tags: ['Research', 'Technology', 'PLA', 'Procurement', 'Suppliers'],
    buttonLabel: 'Open Defense Technology Intelligence',
    route: '/capabilities/defense-technology-intelligence',
  },
];

// Thematic vignette definitions — each queries both English and Chinese keywords
const VIGNETTES: VignetteConfig[] = [
  {
    eyebrow: 'Emerging Technology',
    title: 'Artificial Intelligence',
    description: 'Machine learning, neural networks, and autonomous systems',
    icon: '🤖',
    accentColor: '#6366f1',
    criteria: [
      {
        field: 'keywords',
        q: 'artificial intelligence',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '人工智能',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: 'machine learning',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '机器学习',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Strategic Technology',
    title: 'Quantum Technology',
    description: 'Quantum computing, communication, and sensing research',
    icon: '⚛️',
    accentColor: '#22d3ee',
    criteria: [
      {
        field: 'keywords',
        q: 'quantum',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '量子',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Defense & Aerospace',
    title: 'Hypersonics',
    description: 'Hypersonic vehicles, propulsion, and aerodynamics',
    icon: '🚀',
    accentColor: '#f97316',
    criteria: [
      {
        field: 'keywords',
        q: 'hypersonic',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '高超声速',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Space Domain',
    title: 'Space & Satellites',
    description: 'Satellite systems, space launch, and orbital capabilities',
    icon: '🛰️',
    accentColor: '#a78bfa',
    criteria: [
      {
        field: 'keywords',
        q: 'satellite',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '卫星',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Life Sciences',
    title: 'Biotechnology',
    description: 'Genomics, synthetic biology, and bio-defense research',
    icon: '🧬',
    accentColor: '#34d399',
    criteria: [
      {
        field: 'keywords',
        q: 'biotechnology',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '生物技术',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Maritime & Naval',
    title: 'Undersea Systems',
    description: 'Submarine technology, underwater vehicles, and sonar systems',
    icon: '🌊',
    accentColor: '#38bdf8',
    criteria: [
      {
        field: 'keywords',
        q: 'underwater vehicle',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '水下航行器',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: 'submarine',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Advanced Materials',
    title: 'Nanotechnology',
    description: 'Nanomaterials, nanoelectronics, and nanoscale engineering',
    icon: '🔬',
    accentColor: '#14b8a6',
    criteria: [
      {
        field: 'keywords',
        q: 'nanotechnology',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '纳米技术',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: 'nanomaterial',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '纳米材料',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Energy & Power',
    title: 'Nuclear Technology',
    description: 'Nuclear energy, fusion research, and nuclear propulsion',
    icon: '⚡',
    accentColor: '#facc15',
    criteria: [
      {
        field: 'keywords',
        q: 'nuclear',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '核能',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: 'fusion',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '聚变',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Information Systems',
    title: 'Cybersecurity',
    description: 'Network security, cryptography, and cyber defense',
    icon: '🔐',
    accentColor: '#f43f5e',
    criteria: [
      {
        field: 'keywords',
        q: 'cybersecurity',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '网络安全',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: 'cryptography',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '密码学',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Autonomous Systems',
    title: 'Robotics & Drones',
    description: 'Unmanned systems, autonomous vehicles, and robotic platforms',
    icon: '🤖',
    accentColor: '#8b5cf6',
    criteria: [
      {
        field: 'keywords',
        q: 'robotics',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '机器人',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: 'unmanned aerial vehicle',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'keywords',
        q: '无人机',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
];

// Organization domain vignettes - searches by organization name patterns
const ORGANIZATION_VIGNETTES: VignetteConfig[] = [
  {
    eyebrow: 'Defense Sector',
    title: 'Military Research Institutes',
    description:
      'PLA-affiliated universities and defense research organizations',
    icon: '🏛️',
    accentColor: '#ef4444',
    criteria: [
      {
        field: 'affiliations',
        q: 'National University of Defense Technology',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '国防科技大学',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: 'PLA',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '解放军',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Academia',
    title: 'Chinese Academy of Sciences',
    description: 'CAS institutes and affiliated research centers',
    icon: '🎓',
    accentColor: '#3b82f6',
    criteria: [
      {
        field: 'affiliations',
        q: 'Chinese Academy of Sciences',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '中国科学院',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'State Enterprise',
    title: 'Defense Industry Corporations',
    description: 'AVIC, CASIC, CETC, and other state-owned defense contractors',
    icon: '🏭',
    accentColor: '#f97316',
    criteria: [
      {
        field: 'affiliations',
        q: 'AVIC',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '中国航空工业集团',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: 'CASIC',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '中国航天科工集团',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: 'CETC',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '中国电子科技集团',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Top Universities',
    title: 'C9 League Universities',
    description: "China's elite research universities consortium",
    icon: '🏫',
    accentColor: '#22d3ee',
    criteria: [
      {
        field: 'affiliations',
        q: 'Tsinghua University',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '清华大学',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: 'Peking University',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '北京大学',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: 'Fudan University',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '复旦大学',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Aerospace',
    title: 'Space Research Organizations',
    description: 'CNSA, CASC, and space technology research centers',
    icon: '🚀',
    accentColor: '#a78bfa',
    criteria: [
      {
        field: 'affiliations',
        q: 'China National Space Administration',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '国家航天局',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: 'China Aerospace Science and Technology',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '中国航天科技集团',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
  {
    eyebrow: 'Technology Giants',
    title: 'Private Tech Companies',
    description: 'Huawei, Alibaba, Tencent, and other major tech firms',
    icon: '💼',
    accentColor: '#34d399',
    criteria: [
      {
        field: 'affiliations',
        q: 'Huawei',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '华为',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: 'Alibaba',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '阿里巴巴',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: 'Tencent',
        accuracy: 'Vague',
        operator: 'OR',
      },
      {
        field: 'affiliations',
        q: '腾讯',
        accuracy: 'Vague',
        operator: 'OR',
      },
    ],
  },
];

export default function Home() {
  const classes = useStyles();
  const isDownMediumScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );
  const [exploreTab, setExploreTab] = useState<'technology' | 'organization'>(
    'technology',
  );
  const quickSearchOptions = [
    { label: 'People', route: '/openPerson/A5042449260' },
    {
      label: 'Publications',
      route:
        '/search/publications?criteria=%5B%7B%22field%22%3A%22title%22%2C%22q%22%3A%22hypersonic%22%2C%22operator%22%3A%22OR%22%2C%22accuracy%22%3A%22Vague%22%7D%5D&publicDemo=&publication_date=Descending',
    },
    {
      label: 'Organizations',
      route:
        '/search/organizations?criteria=%5B%7B%22field%22%3A%22literal_name%22%2C%22q%22%3A%22National+University+of+Defense+Technology%22%2C%22operator%22%3A%22AND%22%2C%22accuracy%22%3A%22Vague%22%7D%5D&publicDemo=',
    },
    { label: 'Technologies', route: '/technology/quantum%20radar' },
  ];

  return (
    <div>
      <div className={classes.centerRoot}>
        <Paper
          className={classes.heroCard}
          sx={{ width: '100%', maxWidth: PAGE_MAX_WIDTH, p: { xs: 3, md: 5 } }}
        >
          <Grid container spacing={4} alignItems={'center'}>
            <Grid item xs={12} md={7}>
              <Stack spacing={2.5}>
                <Typography className={classes.eyebrow}>
                  Strategic Intelligence Platform
                </Typography>
                <Typography className={classes.headline}>
                  Search, connect, and operationalize Chinese science and
                  technology intelligence.
                </Typography>
                <Typography className={classes.subline}>
                  Data Abyss helps analysts move from raw entities and
                  publications to strategic understanding across organizations,
                  technologies, talent, research outputs, and web signals.
                </Typography>
                <Box sx={{ pt: 1 }}>
                  <GlobalSearchBar />
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    disableElevation
                    variant={'contained'}
                    className={classes.randomButton}
                    onClick={() => Router.push('/publication/random')}
                  >
                    Random Publication
                  </Button>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ rowGap: 1 }}
                >
                  {quickSearchOptions.map((option) => (
                    <Chip
                      key={option.label}
                      label={option.label}
                      clickable
                      onClick={() => Router.push(option.route)}
                      sx={{
                        color: '#cbd5e1',
                        background: 'rgba(15, 23, 42, 0.65)',
                        border: '1px solid rgba(129, 140, 248, 0.3)',
                        borderRadius: '999px',
                        '&:hover': {
                          background: 'rgba(49, 46, 129, 0.7)',
                        },
                      }}
                    />
                  ))}
                </Stack>
                <Box sx={{ pt: 1.5 }}>
                  <QuickSearchRow />
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack spacing={2.5}>
                <div className={classes.brand}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Fade in={true} timeout={{ enter: 1200, exit: 600 }}>
                      <div>
                        <Image
                          src={'/images/data_abyss_morale_patch.png'}
                          alt="Data Abyss Logo"
                          width={256 * (isDownMediumScreen ? 0.82 : 1.0)}
                          height={256 * (isDownMediumScreen ? 0.82 : 1.0)}
                          quality={95}
                          priority
                        />
                      </div>
                    </Fade>
                  </div>
                </div>
                <Paper className={classes.workflowCard}>
                  <Stack spacing={1.2}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 700 }}>
                      High-value workflows
                    </Typography>
                    <Typography sx={{ color: '#94a3b8' }}>
                      Track risky institutions and affiliated talent.
                    </Typography>
                    <Typography sx={{ color: '#94a3b8' }}>
                      Pivot from publications to organizations and technologies.
                    </Typography>
                    <Typography sx={{ color: '#94a3b8' }}>
                      Prioritize entities using risk and impact signals.
                    </Typography>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </div>

      {/* Capabilities */}
      <Box
        sx={{
          maxWidth: PAGE_MAX_WIDTH,
          mx: 'auto',
          px: { xs: 2, md: 4 },
          pb: 4,
          background: '#020617',
          borderRadius: 3,
          mb: 4,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#818cf8',
                mb: 0.5,
              }}
            >
              Explore the Platform
            </Typography>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 700,
                fontSize: '1.3rem',
                letterSpacing: '-0.02em',
              }}
            >
              Capabilities
            </Typography>
          </Box>
        </Stack>
        <Grid container spacing={3}>
          {CAPABILITIES.map((capability) => (
            <Grid item xs={12} sm={6} lg={4} key={capability.key}>
              <Paper
                className={classes.modeCard}
                onClick={() => Router.push(capability.route)}
                sx={{
                  borderColor: `${capability.color}40 !important`,
                  '&:hover': {
                    borderColor: `${capability.color}8c !important`,
                    boxShadow: `0 0 28px ${capability.color}1f !important`,
                  },
                }}
              >
                <Stack spacing={2} sx={{ height: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        background: `${capability.color}26`,
                        border: `1px solid ${capability.color}4d`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon
                        icon={capability.icon}
                        style={{ color: capability.color, fontSize: 28 }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: capability.color,
                          mb: 0.25,
                        }}
                      >
                        {capability.eyebrow}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#f1f5f9',
                          fontWeight: 700,
                          fontSize: '1.15rem',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {capability.title}
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto !important' }}>
                      <Chip
                        label={capability.badge}
                        size="small"
                        sx={{
                          background: `${capability.color}26`,
                          color: capability.color,
                          border: `1px solid ${capability.color}40`,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                        }}
                      />
                    </Box>
                  </Stack>
                  <Typography
                    className={classes.clampText}
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.9rem',
                      lineHeight: 1.7,
                    }}
                  >
                    {capability.description}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {capability.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          background: 'rgba(15,23,42,0.6)',
                          color: '#94a3b8',
                          border: `1px solid ${capability.color}26`,
                          fontSize: '0.65rem',
                        }}
                      />
                    ))}
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={
                      <Icon
                        icon="carbon:arrow-right"
                        style={{ fontSize: 14 }}
                      />
                    }
                    sx={{
                      alignSelf: 'flex-start',
                      mt: 'auto !important',
                      color: capability.color,
                      borderColor: `${capability.color}59`,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: `${capability.color}99`,
                        backgroundColor: `${capability.color}14`,
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      Router.push(capability.route);
                    }}
                  >
                    {capability.buttonLabel}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Intelligence Vignettes */}
      <Box
        sx={{
          maxWidth: PAGE_MAX_WIDTH,
          mx: 'auto',
          px: { xs: 2, md: 4 },
          pb: 6,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#818cf8',
                mb: 0.5,
              }}
            >
              Intelligence Dashboard
            </Typography>
            <Tabs
              value={exploreTab}
              onChange={(_, newValue) => setExploreTab(newValue)}
              sx={{
                minHeight: 'auto',
                '& .MuiTabs-indicator': {
                  backgroundColor: '#6366f1',
                },
                '& .MuiTab-root': {
                  minHeight: 'auto',
                  padding: '4px 0',
                  marginRight: 3,
                  textTransform: 'none',
                  fontSize: '1.3rem',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: '#64748b',
                  '&.Mui-selected': {
                    color: '#0f172a',
                  },
                  '&:hover': {
                    color: '#334155',
                  },
                },
              }}
            >
              <Tab value="technology" label="Explore by Technology Domain" />
              <Tab
                value="organization"
                label="Explore by Organization Domain"
              />
            </Tabs>
          </Box>
        </Stack>
        <Grid container spacing={3}>
          {(exploreTab === 'technology'
            ? VIGNETTES
            : ORGANIZATION_VIGNETTES
          ).map((vignette) => (
            <Grid item xs={12} md={6} key={vignette.title}>
              <VignetteSection config={vignette} pageSize={6} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </div>
  );
}
